import { useEffect } from 'react';
import { hapticFeedback, viewport } from '@telegram-apps/sdk-react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import clsx from 'clsx';
import { motion } from 'motion/react';
import PropTypes from 'prop-types';
import Button from '@components/ui/Button.component.jsx';
import GlassCard from '@components/ui/GlassCard/GlassCard';
import Hr from '@components/ui/Hr.component.jsx';

const MotionDialogPanel = motion.create(DialogPanel);

const animation = {
  initial: { opacity: 0, y: 50 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 50 },
  transition: { duration: 0.5, ease: 'easeInOut' },
};

function ModalDialog({
  open,
  onClose,
  onButtonClick,
  onSecondaryButtonClick,
  title,
  buttonText,
  secondaryButtonText,
  children,
  variant = 'dark',
  height,
}) {
  useEffect(() => {
    hapticFeedback.selectionChanged();
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-10"></div>
      <div
        className="fixed inset-0 flex w-screen items-center justify-center p-4 z-20"
        style={{
          paddingTop: viewport.contentSafeAreaInsetTop(),
          paddingBottom: viewport.contentSafeAreaInsetBottom(),
        }}
      >
        <MotionDialogPanel
          {...animation}
          // className={clsx(
          //   'max-w-lg w-full space-y-4 rounded-xl p-4 shadow-xl border-event-muted',
          //   {
          //     'bg-event-light/95 border-corporate-gray/20': variant === 'light',
          //     'bg-event-deep/95 border-corporate-dark/20': variant === 'dark',
          //     'bg-[image:var(--color-event-gradient)]': variant === 'gradient',
          //   },
          // )}
        >
          <GlassCard
            className={'max-w-lg w-full space-y-4 rounded-2xl p-4 shadow-xl'}
          >
            <DialogTitle
              className={clsx('font-wide text-xl font-semibold text-center', {
                'text-event-deep-dark/80': variant === 'light',
                'text-event-light': variant !== 'light',
              })}
            >
              {title}
            </DialogTitle>
            <Hr variant={variant === 'light' ? 'dark' : 'light'} />
            <div
              className={clsx(
                'overflow-y-auto max-h-[70vh] rounded-lg',
                height,
                {
                  'text-event-light': variant !== 'light',
                  'text-event-deep-dark/80': variant === 'light',
                },
              )}
            >
              {children}
            </div>

            {(buttonText || secondaryButtonText) && (
              <div className="flex justify-center gap-1">
                {secondaryButtonText && (
                  <Button
                    className="h-12 flex-1 flex items-center justify-center text-center font-medium text-nowrap"
                    onButtonClick={onSecondaryButtonClick ?? onClose}
                    buttonText={secondaryButtonText}
                    variant="secondary"
                  />
                )}
                {buttonText && (
                  <Button
                    className="h-12 flex-1 flex items-center justify-center text-center font-medium text-nowrap"
                    onButtonClick={onButtonClick ?? onClose}
                    buttonText={buttonText}
                    variant="primary"
                  />
                )}
              </div>
            )}
          </GlassCard>
        </MotionDialogPanel>
      </div>
    </Dialog>
  );
}

ModalDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  buttonText: PropTypes.string,
  children: PropTypes.node,
  variant: PropTypes.oneOf(['dark', 'light', 'gradient']),
};

export default ModalDialog;
